import { Box, List } from '@mui/material';
import classNames from 'classnames';
import bind from 'memoize-bind';
import React, { Children, PureComponent, ReactNode } from 'react';

import PlusIcon from '@sbu/assets.svg/src/add.svg';
import MinusIcon from '@sbu/assets.svg/src/remove.svg';
import { Divider, IconButton } from '@sumologic/yama-ds-components';

import { Collapse } from 'shared/components/layout/Collapse';

import css from './TreeNode.module.scss';

export interface TreeNodeProps {
  label: ReactNode;
  className?: any;
  expanded?: boolean;
  disabled?: boolean;
  onExpand: () => void;
  onCollapse: () => void;
}

interface TreeNodeState {
  expanded?: boolean;
}

export class TreeNode extends PureComponent<
  React.PropsWithChildren<TreeNodeProps>,
  TreeNodeState
> {
  static defaultProps: Partial<TreeNodeProps> = {
    onExpand: () => undefined,
    onCollapse: () => undefined,
  };

  constructor(props: TreeNodeProps) {
    super(props);

    this.state = {
      expanded: props.expanded,
    };
  }

  onButtonClick() {
    const { onExpand, onCollapse } = this.props;
    const { expanded } = this.state;
    const newState = !expanded;

    this.setState(
      {
        expanded: newState,
      },
      () => {
        if (newState) {
          onExpand();
        } else {
          onCollapse();
        }
      }
    );
  }

  render() {
    const { children, className, label, disabled } = this.props;
    const { expanded } = this.state;
    const hasChildren = children && Children.count(children) > 0;
    const Icon = expanded ? MinusIcon : PlusIcon;

    return (
      <li
        className={classNames(className, css.node, {
          [css.leaf]: !hasChildren,
          [css.disabled]: disabled,
        })}
      >
        {hasChildren && (
          <IconButton
            className={css.button}
            disabled={disabled}
            onClick={bind(this.onButtonClick, this)}
            title={expanded ? 'Collapse children' : 'Expand children'}
          >
            <Icon className={css.icon} />
          </IconButton>
        )}
        <span className={css.label}>{label}</span>
        {hasChildren && (
          <Collapse expanded={expanded}>
            <Box margin='10px 0 10px 30px' padding={0} position='relative'>
              <Divider
                orientation='vertical'
                sx={{
                  left: 0,
                  position: 'absolute',
                }}
              />
              <List
                sx={{
                  listStyle: 'none',
                }}
              >
                {children}
              </List>
            </Box>
          </Collapse>
        )}
      </li>
    );
  }
}
